import { memorySize } from "./memorySize";

let inMemoryCache = {};
const timers = {};
let defaultInMemoryCacheTime;
let memoryCacheLimit;

/**
 * Returns if the memory cache limit has been reached
 */
function isMemoryLimitReached() {
  return memorySize(inMemoryCache).getBytes() > memoryCacheLimit;
}

/**
 * If the memory cache limit has been reached, removes the cached data in order as follow:
 * - Genres (full catalog)
 * - Seasons
 * - Titles
 */
function removeItemsFromMemory() {
  const paterns = {
    titles: /^\d+$/,
    genres: /genres-/,
    seasons: /^\d+(-)+\w*/
  };
  const remove = pattern => Object.keys(inMemoryCache).forEach(key => pattern.test(key) && delete inMemoryCache[key]);
  remove(paterns.genres);
  isMemoryLimitReached() && remove(paterns.seasons);
  isMemoryLimitReached() && remove(paterns.titles);
}

/**
 * Stores data in memory organized as key-value pairs. Given that the data is stored in memory, it will be flushed every
 * time the page is refreshed. Aldo, it has a mechanism that removes the stored keys once a given amount of time is
 * reached (default is 5 minutes). This cache is useful when the persistent cache is not available, for example.
 * @type {Object}
 * @private
 */
export const memoryCache = {
  /**
   * Initialized the in memory cache by setting up a default timeout for the stored values.
   * @param {Number} defaultTime An amount of time in milliseconds that will be used to remove the keys from the memory.
   * Each key that is stored using the set function will last the time specified by this parameter unless the call to
   * set itself overrides the default value.
   * @param {Number} cacheLimit If the memory cache reaches this limit, some cached data over the limit will be removed.
   */
  init(defaultTime, cacheLimit) {
    defaultInMemoryCacheTime = defaultTime;
    memoryCacheLimit = cacheLimit;
  },
  /**
   * Returns the value corresponding to a key if it is present in the memory cache.
   * @param {String} key The key whose value is being looked for in the memory cache.
   * @return {Object} The value in the memory corresponding to the received key or undefined if the key is not present.
   */
  get(key) {
    return inMemoryCache[key];
  },
  /**
   * Stores a key-value pair in memory. Once the pair has been stored, a timer is stared in order to remove it from
   * memory. The default value is 5 minutes but it can be configured by specifying a different time in the config
   * parameter.
   * If the memory cache limit has been reached, calls to method which removes some cached data over the limit.
   * @param {String} key The name that will identify the value that is being stored.
   * @param {Object} value An arbitrary data type that will be stoed in the memory cache.
   * @param {Object} config Configuration object to define aspects of how the value will be stored.
   * @param {Number} config.expiredIn Time after which the stored value should be removed from memory. It's used to
   * override the default 5 minutes expiration time. Should be provided in milliseconds.
   */
  set(key, value, config = {}) {
    const expirationTime = config.expiresIn || defaultInMemoryCacheTime;
    memoryCacheLimit && isMemoryLimitReached() && removeItemsFromMemory();
    inMemoryCache[key] = value;
    clearTimeout(timers[key]);
    timers[key] = setTimeout(() => {
      delete inMemoryCache[key];
      delete timers[key];
      typeof config.callback === "function" && config.callback();
    }, expirationTime);
  },
  /**
   * Removes the value corresponding to a given key from the memory cache, if it exists. If the key is not present in
   * the memory cache, this function does nothing. The Key can be either a string or a RegExp. In the first case a full
   * match will be performed to find the value to remove, in the second case, every value matching the regular
   * expression will be removed.
   * @param {String|RegExp} key The key whose corresponding value needs to be removed from the memory cache or a regular
   * expression that matches all the values to be removed from the memory.
   */
  remove(key) {
    function removeItemFromMemoryObject(key) {
      inMemoryCache[key] && delete inMemoryCache[key];
      if (timers[key]) {
        clearTimeout(timers[key]);
        delete timers[key];
      }
    }
    if (typeof key === "string") {
      removeItemFromMemoryObject(key);
    } else if (key instanceof RegExp) {
      // TODO Consider doing this in a web worker if the inMemoryCache has too many properties, to avoid blocking the main thread
      Object.keys(inMemoryCache).forEach(inMemoryItem => {
        if (inMemoryItem.match(key)) {
          removeItemFromMemoryObject(inMemoryItem);
        }
      });
    }
  },
  /**
   * Removes all the entries from the memory cache and restarts it to the initial status.
   */
  clear() {
    inMemoryCache = {};
  }
};
